import React from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { newReportConfig, getDateRangeFromAllowed, getLabelFromAllowed } from "./mdmReportsUtils";

interface DateRangeSelectorProps {
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    setFromDate: (date: Dayjs | null) => void;
    setToDate: (date: Dayjs | null) => void;
    reportConfig?: newReportConfig | null;
    isDistributorView?: boolean;
}

// Helper function to get financial year start date (April 1st)
const getFinancialYearStart = (today: Dayjs): Dayjs => {
    const currentMonth = today.month(); // 0-indexed (0 = January, 3 = April)
    const currentYear = today.year();
    
    // If current month is before April (0-2), financial year started last year
    if (currentMonth < 3) {
        return dayjs(`${currentYear - 1}-04-01`);
    }
    // Otherwise, financial year started this year
    return dayjs(`${currentYear}-04-01`);
};

// Helper function to get max allowed date based on view type
const getMaxAllowedDate = (isDistributorView: boolean, today: Dayjs): Dayjs => {
    if (isDistributorView) {
        return today;
    }
    return today;
};

// Helper function to get min allowed date based on view type
const getMinAllowedDate = (isDistributorView: boolean, today: Dayjs): Dayjs => {
    if (isDistributorView) {
        return getFinancialYearStart(today);
    }
    return today.subtract(1, "year");
};

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ 
    fromDate, 
    toDate, 
    setFromDate, 
    setToDate, 
    reportConfig = null,
    isDistributorView = false 
}) => {
    const today = React.useMemo(() => dayjs(), []);
    
    const dateRangeAllowed = reportConfig?.dateRangeAllowed;
    const hasAllowedDateRange = React.useMemo(() => 
        !!dateRangeAllowed && typeof dateRangeAllowed === 'string' && dateRangeAllowed.trim().length > 0,
        [dateRangeAllowed]
    );

    const showLast7DaysFilter = reportConfig?.showLast7DaysFilter ?? true;
    const showLast3MonthsFilter = reportConfig?.showLast3MonthsFilter ?? true;
    const shouldShowCustomDateFilter = reportConfig?.shouldShowCustomDateFilter ?? true;
    
    const defaultSelectedOption = React.useMemo(() => {
        if (shouldShowCustomDateFilter) return 'custom';
        if (hasAllowedDateRange) return 'allowed';
        if (showLast7DaysFilter) return 'last7';
        if (showLast3MonthsFilter) return 'last3months';
        return 'custom'; 
    }, [shouldShowCustomDateFilter, hasAllowedDateRange, showLast7DaysFilter, showLast3MonthsFilter]);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [displayText, setDisplayText] = React.useState("Select Custom Date Range");
    const [selectedOption, setSelectedOption] = React.useState<'last7' | 'last3months' | 'custom' | 'allowed'>(defaultSelectedOption);
    const [range, setRange] = React.useState<[Dayjs | null, Dayjs | null]>([dayjs(), dayjs()]);

    const open = Boolean(anchorEl);
    
    // Calculate absolute min/max dates based on view type
    const absoluteMinDate = React.useMemo(() => getMinAllowedDate(isDistributorView, today), [isDistributorView, today]);
    const absoluteMaxDate = React.useMemo(() => getMaxAllowedDate(isDistributorView, today), [isDistributorView, today]);
    
    // Calculate last 7 days range (respecting absolute boundaries)
    const last7Min = React.useMemo(() => {
        const calculated = today.subtract(7, "day");
        return calculated.isBefore(absoluteMinDate) ? absoluteMinDate : calculated;
    }, [today, absoluteMinDate]);
    
    // Calculate last 3 months range (respecting absolute boundaries)
    const last3MonthsMin = React.useMemo(() => {
        const calculated = today.subtract(3, "month");
        return calculated.isBefore(absoluteMinDate) ? absoluteMinDate : calculated;
    }, [today, absoluteMinDate]);
    
    // Calculate allowed date range if configured (respecting absolute boundaries)
    const allowedRange = React.useMemo(() => {
        let allowedMin = absoluteMinDate;
        let allowedMax = absoluteMaxDate;
        let allowedLabel = "Custom Period";
        
        if (hasAllowedDateRange && dateRangeAllowed) {
            const calculatedRange = getDateRangeFromAllowed(dateRangeAllowed, today.toDate());
            if (calculatedRange) {
                let startDate = dayjs(calculatedRange.start);
                let endDate = dayjs(calculatedRange.end);
                
                // Clamp dates to absolute boundaries
                if (startDate.isBefore(absoluteMinDate, "day")) {
                    startDate = absoluteMinDate;
                }
                if (endDate.isAfter(absoluteMaxDate, "day")) {
                    endDate = absoluteMaxDate;
                }
                
                // Ensure start is not after end
                if (startDate.isAfter(endDate, "day")) {
                    startDate = endDate;
                }
                
                allowedMin = startDate;
                allowedMax = endDate;
                allowedLabel = getLabelFromAllowed(dateRangeAllowed);
            }
        }
        
        return { allowedMin, allowedMax, allowedLabel };
    }, [hasAllowedDateRange, dateRangeAllowed, today, absoluteMinDate, absoluteMaxDate]);
    
    
    React.useEffect(() => {
        if (selectedOption === 'last7' && showLast7DaysFilter) {
            const startDate = last7Min;
            const endDate = today;
            setRange([startDate, endDate]);
            setFromDate(startDate);
            setToDate(endDate);
            setDisplayText(`${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`);
        } else if (selectedOption === 'last3months' && showLast3MonthsFilter) {
            const startDate = last3MonthsMin;
            const endDate = today;
            setRange([startDate, endDate]);
            setFromDate(startDate);
            setToDate(endDate);
            setDisplayText(`${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`);
        } else if (selectedOption === 'allowed' && hasAllowedDateRange) {
            const startDate = allowedRange?.allowedMin;
            const endDate = allowedRange?.allowedMax;
            setRange([startDate, endDate]);
            setFromDate(startDate);
            setToDate(endDate);
            setDisplayText(`${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`);
        } else if (selectedOption === 'custom' && shouldShowCustomDateFilter) {
            const defaultDate = today;
            setRange([defaultDate, defaultDate]);
            setFromDate(defaultDate);
            setToDate(defaultDate);
            setDisplayText(`${defaultDate.format("DD MMM YYYY")} - ${defaultDate.format("DD MMM YYYY")}`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOption]);

    React.useEffect(() => {
        if (fromDate && toDate) {
            setRange([fromDate, toDate]);
            setDisplayText(`${fromDate.format("DD MMM YYYY")} - ${toDate.format("DD MMM YYYY")}`);
        }
    }, [fromDate, toDate]);

    React.useEffect(() => {
        const isCurrentOptionValid = 
            (selectedOption === 'allowed' && hasAllowedDateRange) ||
            (selectedOption === 'last7' && showLast7DaysFilter) ||
            (selectedOption === 'last3months' && showLast3MonthsFilter) ||
            (selectedOption === 'custom' && shouldShowCustomDateFilter);
        
        if (!isCurrentOptionValid) {
            setSelectedOption(defaultSelectedOption);
        }
    }, [selectedOption, hasAllowedDateRange, showLast7DaysFilter, showLast3MonthsFilter, shouldShowCustomDateFilter, defaultSelectedOption]);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleOptionSelect = (option: 'last7' | 'last3months' | 'custom' | 'allowed') => {
        setSelectedOption(option);
    };

    const handleRangeChange = (newRange: [unknown, unknown]) => {
        const start = newRange[0] as Dayjs | null;
        const end = newRange[1] as Dayjs | null;
        setRange([start, end]);
        if (start && end) {
            setFromDate(start);
            setToDate(end);
            setDisplayText(`${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`);
            handleClose();
        }
    };

    // Calculate min/max dates for calendar navigation based on selected option
    let minDate: Dayjs = absoluteMinDate;
    let maxDate: Dayjs = absoluteMaxDate;
    
    if (selectedOption === 'last7' && showLast7DaysFilter) {
        minDate = last7Min;
        maxDate = today;
    } else if (selectedOption === 'last3months' && showLast3MonthsFilter) {
        minDate = last3MonthsMin;
        maxDate = today;
    } else if (selectedOption === 'allowed' && hasAllowedDateRange) {
        minDate = allowedRange.allowedMin;
        maxDate = allowedRange.allowedMax;
    } else if (selectedOption === 'custom' && shouldShowCustomDateFilter) {
        minDate = absoluteMinDate;
        maxDate = absoluteMaxDate;
    }    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                    variant="outlined"
                    onClick={handleOpen}
                    sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        background: "#fff",
                        fontWeight: 600,
                        borderColor: "#ccc",
                        color: "#333",
                    }}
                >
                    {displayText}
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    sx={{ mt: 1 }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <Box
                            sx={{
                                width: "145px",
                                borderRight: "1px dotted #ccc",
                            }}
                        >
                            {hasAllowedDateRange && (
                                <MenuItem
                                    onClick={() => handleOptionSelect('allowed')}
                                    sx={{ fontSize: "13px", background: selectedOption === 'allowed' ? '#f5f5f5' : undefined, fontWeight: selectedOption === 'allowed' ? 600 : 400 }}
                                >
                                    {allowedRange.allowedLabel}
                                </MenuItem>
                            )}
                            {showLast7DaysFilter && (
                                <>
                                    <MenuItem
                                        onClick={() => handleOptionSelect('last7')}
                                        sx={{ fontSize: "13px", background: selectedOption === 'last7' ? '#f5f5f5' : undefined, fontWeight: selectedOption === 'last7' ? 600 : 400 }}
                                    >
                                        Last 7 days
                                    </MenuItem>
                                    <Box
                                        sx={{
                                            borderBottom: "1px dotted #ccc",
                                        }}
                                    />
                                </>
                            )}
                            {showLast3MonthsFilter && (
                                <>
                                    <MenuItem
                                        onClick={() => handleOptionSelect('last3months')}
                                        sx={{ fontSize: "13px", background: selectedOption === 'last3months' ? '#f5f5f5' : undefined, fontWeight: selectedOption === 'last3months' ? 600 : 400 }}
                                    >
                                        Last 3 months
                                    </MenuItem>
                                    <Box
                                        sx={{
                                            borderBottom: "1px dotted #ccc",
                                        }}
                                    />
                                </>
                            )}
                            {shouldShowCustomDateFilter && (
                                <MenuItem
                                    onClick={() => handleOptionSelect('custom')}
                                    sx={{ fontSize: "13px", background: selectedOption === 'custom' ? '#f5f5f5' : undefined, fontWeight: selectedOption === 'custom' ? 600 : 400 }}
                                >
                                    Custom Date Filter
                                </MenuItem>
                            )}
                            
                        </Box>
                        <Box sx={{ background: "#fff", borderRadius: 2, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', gap: 0, padding: 0, width: "450px", overflow: 'visible', height: "230px" }}>
                            <Box sx={{ transform: 'scale(0.7)', transformOrigin: 'top left', display: 'inline-block' }}>
                                <StaticDatePicker
                                    displayStaticWrapperAs="desktop"
                                    value={range[0]}
                                    onChange={(date: Dayjs | null) => {
                                        if (!date) return;

                                        // Enforce absolute boundaries
                                        if (date.isBefore(minDate, "day") || date.isAfter(maxDate, "day")) {
                                            return;
                                        }

                                        if (range[1] && date.isAfter(range[1], "day")) {
                                            setRange([date, date]);
                                            setFromDate(date);
                                            setToDate(date);
                                            setDisplayText(`${date.format("DD MMM YYYY")} - ${date.format("DD MMM YYYY")}`);
                                            return;
                                        }

                                        setRange([date, range[1]]);
                                        setFromDate(date);
                                        if (range[1]) {
                                            setDisplayText(`${date.format("DD MMM YYYY")} - ${range[1].format("DD MMM YYYY")}`);
                                        }
                                    }}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    disableFuture
                                    shouldDisableDate={(d) => {
                                        // Disable dates outside the allowed range
                                        if (d.isBefore(minDate, "day") || d.isAfter(maxDate, "day")) {
                                            return true;
                                        }
                                        // Also disable dates after the selected end date
                                        if (!range[1]) return false;
                                        return d.isAfter(range[1], "day"); 
                                    }}
                                    renderInput={(params) => <input {...params.inputProps} style={{ display: "none" }} />}
                                />
                            </Box>
                            <Box sx={{ transform: 'scale(0.7)', transformOrigin: 'top left', display: 'inline-block', marginLeft: '-100px' }}>
                                <StaticDatePicker
                                    displayStaticWrapperAs="desktop"
                                    value={range[1]}
                                    onChange={(date: Dayjs | null) => {
                                        if (!date) return;

                                        // Enforce absolute boundaries
                                        if (date.isBefore(minDate, "day") || date.isAfter(maxDate, "day")) {
                                            return;
                                        }

                                        if (range[0] && date.isBefore(range[0], "day")) {
                                            setRange([date, date]);
                                            setFromDate(date);
                                            setToDate(date);
                                            setDisplayText(`${date.format("DD MMM YYYY")} - ${date.format("DD MMM YYYY")}`);
                                            return;
                                        }

                                        setRange([range[0], date]);
                                        setToDate(date);

                                        if (range[0]) {
                                            setDisplayText(`${range[0].format("DD MMM YYYY")} - ${date.format("DD MMM YYYY")}`);
                                        }
                                        handleClose();
                                    }}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    disableFuture
                                    shouldDisableDate={(d) => {
                                        // Disable dates outside the allowed range
                                        if (d.isBefore(minDate, "day") || d.isAfter(maxDate, "day")) {
                                            return true;
                                        }
                                        // Also disable dates before the selected start date
                                        if (!range[0]) return false;
                                        return d.isBefore(range[0], "day");
                                    }}
                                    renderInput={(params) => <input {...params.inputProps} style={{ display: "none" }} />}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Menu>
            </Box>
        </LocalizationProvider>
    );
};

export default DateRangeSelector;